import { ApiResponse } from "interfaces/models/api";
import { API_GET_CACHE, UpdateToOnlineStatus } from "constants/serviceWorker";
import {
  ChangedResponseData,
  ErrorResponse,
} from "../../BaseRequestServiceWorker";
import { matchPath } from "react-router-dom";
import { DATA_STORE, Operation } from "constants/serviceWorker";
import { iCachedItem } from "interfaces/models/serviceWorker";
import BaseCustomResponse from "../../BaseCustomResponse";
import { matchToCache } from "libs/service-worker/cache";
import { LIST_PATH_API_DETAIL } from "constants/api";

export enum OrderAddItem {
  LAST_PAGE = 1,
  FIRST_PAGE = 2,
}

const NUMBER_REQUEST_RELATIVE = 1;

export default class BaseCustomGetResponse extends BaseCustomResponse {
  protected defaultNumberRequestRelative = NUMBER_REQUEST_RELATIVE;
  protected orderAddItem = OrderAddItem.LAST_PAGE;

  async getResponse() {
    const request = this.event.request.clone();
    let response: Response | undefined;
    // network first
    if (this.swOnlineStatus) {
      const req = await this.getRequestForFetch();
      response = await fetch(req).catch((err) => {
        console.log(err);

        return ErrorResponse.clone();
      });
    } else {
      response = await matchToCache(API_GET_CACHE, request);
    }
    const responseJson = await response?.json().catch(() => undefined);
    const apiResponse: any = responseJson || {
      data: [],
    };

    return { response, apiResponse };
  }

  isLastPage(apiResponse: ApiResponse, params: Record<string, any>) {
    const totalPage = params?.["total"];
    const currentPage = params?.["page"];
    const paging = params?.["paging"];

    return (
      (!apiResponse?.pagination && paging === "cursor") ||
      (totalPage === currentPage && totalPage !== undefined)
    );
  }

  overrideCachedData(params: {
    cachedItem: any;
    currentItem: any;
    status: UpdateToOnlineStatus;
  }) {
    const { cachedItem, currentItem, status } = params;
    const isStatusSuccess = status === UpdateToOnlineStatus.Success;
    let result = structuredClone(currentItem) as any;
    // when online or after sync data success
    // we can remove offline id of record
    if ((this.swOnlineStatus || isStatusSuccess) && "offlineId" in cachedItem) {
      delete cachedItem?.offlineId;
    }
    result = this.swOnlineStatus
      ? { ...cachedItem, ...currentItem }
      : { ...currentItem, ...cachedItem };

    return result;
  }

  filterCachedItem(args: { cachedItem: iCachedItem; validParams: string[] }) {
    const {
      cachedItem: { operation, ...item },
      validParams,
    } = args;
    // with case update -> only cache some field update -> can ignore verify params
    if (operation === Operation.Patch) {
      return true;
    }
    if (operation === Operation.Delete) {
      return !(
        validParams.includes("isDeleted") && this.params["isDeleted"] === "true"
      );
    }
    const checkValidParams = validParams.every((key) => {
      const itemDataKey = key;
      let itemData = (item.data as any)[itemDataKey];
      const paramsKey = key;
      const paramsData = decodeURIComponent(this.params[paramsKey]);
      if (paramsKey === "isDeleted") {
        itemData = (item.data as any)[itemDataKey] ?? "false";
        // same as above
      } else if (["page", "total", "paging"].includes(paramsKey)) {
        itemData = paramsData;
      }

      return paramsData === itemData;
    });

    return checkValidParams;
  }

  getCachedData(cachedItem: iCachedItem) {
    return cachedItem?.data;
  }

  async changeResponseListData(p: {
    apiResponse: ApiResponse;
    response: Response | undefined;
  }) {
    const indexedDb = await this.getIndexedDb();
    const { apiResponse: _apiResponse, response } = p;
    const apiResponse = structuredClone(_apiResponse);
    const params = this.params;

    // validate params of request
    const validParams = Object.keys(params).filter((key) => !!params[key]);
    const isLastPage = this.isLastPage(apiResponse, params);
    // handle override or add new data from cache
    const _cachedItems = await indexedDb?.getAll(
      DATA_STORE,
      this.storeNameOfRequest
    );
    if (!_cachedItems?.length) {
      return { data: apiResponse, response };
    }
    const deleteItemIdsSet: Set<string> = new Set();
    const cachedItems = _cachedItems.filter((cachedItem) => {
      if (cachedItem.operation === Operation.Delete) {
        if (Array.isArray(cachedItem.data)) {
          cachedItem.data.forEach((deleteId) => {
            deleteItemIdsSet.add(deleteId);
          });
        } else {
          deleteItemIdsSet.add(cachedItem.data);
        }
      }
      const isValidItem = this.filterCachedItem({ cachedItem, validParams });

      return isValidItem;
    });

    // check data cache is push in other request
    // (eg: data cache is used for get document Tasks api or get documentTasksByTitle api)

    for await (const data of cachedItems) {
      const cachedItem = data;
      const cachedData = this.getCachedData(cachedItem);

      const cachedItemId =
        cachedItem.operation === Operation.Delete ? cachedData : cachedData.id;
      const existed = apiResponse?.data?.find(
        (item: any) => item.id === cachedItemId
      );
      switch (cachedItem.operation) {
        case Operation.Patch:
        case Operation.Post:
          if (deleteItemIdsSet.has(cachedItem.data.id)) {
            break;
          }
          // condition override data for request get list
          // condition add new data for request get list
          const isAddNewData =
            !existed &&
            // If requestName is log then it will be added at the beginning
            // otherwise it will be left at the end
            ((isLastPage && this.orderAddItem === OrderAddItem.LAST_PAGE) ||
              this.orderAddItem === OrderAddItem.FIRST_PAGE) &&
            cachedItem.operation === Operation.Post;

          if (existed) {
            apiResponse.data = (apiResponse?.data || []).map((item: any) => {
              if (item.id === cachedData.id) {
                return this.overrideCachedData({
                  cachedItem: cachedData,
                  currentItem: item,
                  status: cachedItem.status,
                });
              }

              return item;
            });
          } else if (isAddNewData) {
            apiResponse?.data?.push(cachedData);
          }
          break;
        case Operation.Delete:
          if (existed || isLastPage) {
            if (!this.swOnlineStatus) {
              apiResponse.data = apiResponse?.data.filter(
                (item: any) => item.id !== cachedData
              );
            }
          }
          break;
        default:
          break;
      }
    }

    return { data: apiResponse, response };
  }

  async changeResponseDetailData(params: {
    apiResponse: ApiResponse;
    response: Response | undefined;
  }): Promise<ChangedResponseData> {
    const indexedDb = await this.getIndexedDb();
    const { apiResponse: _apiResponse, response } = params;
    const apiResponse = structuredClone(_apiResponse);
    const cachedItems = (
      await indexedDb?.getAll(DATA_STORE, this.storeNameOfRequest)
    )?.sort((a, b) => a.requestTime - b.requestTime);

    if (!cachedItems?.length) {
      return { data: apiResponse, response };
    }
    const detailId =
      apiResponse?.data?.id ||
      new URL(this.event.request.clone().url).pathname.split("/").pop() ||
      ""; // get id from url when there is not response;
    const cachedItemsValid = cachedItems.filter(
      (item) => item?.data?.id === detailId
    );

    for await (const item of cachedItemsValid) {
      apiResponse.data = this.overrideCachedData({
        cachedItem: item.data,
        currentItem: apiResponse?.data,
        status: item.status,
      });
    }

    return { data: apiResponse, response };
  }

  async changeResponseData(): Promise<Response> {
    const { apiResponse, response } = await this.getResponse();
    const request = this.event.request.clone();
    let isDetailApi = !Array.isArray(apiResponse?.data) && apiResponse?.data;
    if (!isDetailApi) {
      // case offline and not have cache data -> we must check by list url
      const baseApiPath = process.env.REACT_APP_API_HOST_URL || "";
      const currentPath = request.url.replace(baseApiPath, "").split("?")[0];
      isDetailApi = LIST_PATH_API_DETAIL.some((path) =>
        matchPath(path, currentPath)
      );
    }
    let newResponse: ChangedResponseData;
    if (isDetailApi) {
      newResponse = await this.changeResponseDetailData({
        apiResponse,
        response,
      });
    } else {
      newResponse = await this.changeResponseListData({
        apiResponse,
        response,
      });
    }

    return await this.response(newResponse);
  }
}
